import React, { useState, useCallback, useEffect } from 'react';
import {
    Box, Card, CardHeader, Button, Typography, CircularProgress, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Checkbox, Chip, Paper, Stack, Tooltip, IconButton, Collapse, LinearProgress,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    TextField, InputAdornment
} from '@mui/material';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import SearchIcon from '@mui/icons-material/Search';
import { getClients, bulkTriggerUpdate } from './api';

export default function OTAUpdatePanel({ apiKey, onError }) {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState('');
    const [selectedClients, setSelectedClients] = useState([]);
    const [updateResults, setUpdateResults] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [expandedHistory, setExpandedHistory] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // all, updateAvailable, updating, upToDate

    const fetchClients = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getClients(apiKey, 1, 0, {}); // Get all clients (limit=0 means no pagination)
            setClients(data || []);
            if (onError) onError(null);
        } catch (err) {
            const errorMsg = err.data?.error || err.message || 'Failed to fetch clients';
            setError(errorMsg);
            if (onError) onError(errorMsg);
        } finally {
            setLoading(false);
        }
    }, [apiKey, onError]);

    // Compute whether any clients are updating (for faster polling)
    const hasUpdatingClients = clients.some(c => c.isUpdating);
    const updatingClientIds = clients.filter(c => c.isUpdating).map(c => c.id);
    const updatingClientIdsKey = updatingClientIds.join(',');

    useEffect(() => {
        fetchClients();
        // Auto-refresh every 30 seconds normally, or every 3 seconds during active updates
        const refreshInterval = hasUpdatingClients ? 3000 : 30000;
        const interval = setInterval(fetchClients, refreshInterval);
        return () => clearInterval(interval);
    }, [fetchClients, hasUpdatingClients]);

    // SSE connections for individual updating clients
    useEffect(() => {
        if (updatingClientIds.length === 0) return;

        const eventSources = {};
        let isMounted = true;

        updatingClientIds.forEach(clientId => {
            try {
                const url = `https://api1.masternode.wobot.ai/api/clients/${clientId}/update-status-stream?token=${encodeURIComponent(apiKey)}`;
                const eventSource = new EventSource(url);

                eventSource.onmessage = (event) => {
                    if (!isMounted) return;
                    try {
                        const data = JSON.parse(event.data);
                        // Update the specific client in our clients array
                        setClients(prevClients => prevClients.map(client => {
                            if (client.id === clientId) {
                                return {
                                    ...client,
                                    isUpdating: data.isUpdating,
                                    updateProgress: data.updateProgress,
                                    agentVersion: data.agentVersion || client.agentVersion,
                                };
                            }
                            return client;
                        }));

                        // If update completed, close SSE and refresh to get new data
                        if (!data.isUpdating) {
                            eventSource.close();
                            delete eventSources[clientId];
                            setTimeout(() => {
                                if (isMounted) fetchClients();
                            }, 1000);
                        }
                    } catch (err) {
                        console.error('[SSE] Failed to parse message:', err);
                    }
                };

                eventSource.onerror = () => {
                    console.warn(`[SSE] Connection error for client ${clientId}, will rely on polling`);
                    eventSource.close();
                    delete eventSources[clientId];
                };

                eventSources[clientId] = eventSource;
            } catch (err) {
                console.error(`[SSE] Failed to create EventSource for ${clientId}:`, err);
            }
        });

        return () => {
            isMounted = false;
            Object.values(eventSources).forEach(es => es.close());
        };
    }, [updatingClientIds, updatingClientIdsKey, apiKey, fetchClients]);

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const eligibleClients = filteredClients
                .filter(c => c.updateAvailable && c.status === 'Online' && !c.isUpdating)
                .map(c => c.id);
            setSelectedClients(eligibleClients);
        } else {
            setSelectedClients([]);
        }
    };

    const handleSelectClient = (clientId) => {
        setSelectedClients(prev =>
            prev.includes(clientId)
                ? prev.filter(id => id !== clientId)
                : [...prev, clientId]
        );
    };

    const handleBulkUpdate = async () => {
        if (selectedClients.length === 0) return;

        setUpdating(true);
        setError('');
        setUpdateResults(null);

        try {
            const result = await bulkTriggerUpdate(apiKey, selectedClients);
            setUpdateResults(result);
            setSelectedClients([]);
            setConfirmDialogOpen(false);

            // Refresh clients after a short delay
            setTimeout(() => {
                fetchClients();
            }, 2000);
        } catch (err) {
            const errorMsg = err.data?.error || err.message || 'Failed to trigger bulk update';
            setError(errorMsg);
            if (onError) onError(errorMsg);
        } finally {
            setUpdating(false);
        }
    };

    const toggleHistoryExpanded = (clientId) => {
        setExpandedHistory(prev => ({
            ...prev,
            [clientId]: !prev[clientId]
        }));
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'online': return 'success';
            case 'offline': return 'default';
            case 'blocked': return 'error';
            default: return 'default';
        }
    };

    const getUpdateStatusIcon = (client) => {
        if (client.isUpdating) {
            return <PendingIcon color="warning" />;
        } else if (client.updateAvailable) {
            return <SystemUpdateIcon color="primary" />;
        } else {
            return <CheckCircleIcon color="success" />;
        }
    };

    const getHistoryStatusIcon = (status) => {
        switch (status) {
            case 'complete':
                return <CheckCircleIcon color="success" fontSize="small" />;
            case 'error':
                return <ErrorIcon color="error" fontSize="small" />;
            case 'in-progress':
                return <PendingIcon color="warning" fontSize="small" />;
            default:
                return null;
        }
    };

    // Filter clients
    const filteredClients = clients.filter(client => {
        // Search filter
        const matchesSearch = searchTerm === '' ||
            client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            client.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            client.company?.name?.toLowerCase().includes(searchTerm.toLowerCase());

        // Status filter
        let matchesStatus = true;
        switch (filterStatus) {
            case 'updateAvailable':
                matchesStatus = client.updateAvailable && !client.isUpdating;
                break;
            case 'updating':
                matchesStatus = client.isUpdating;
                break;
            case 'upToDate':
                matchesStatus = !client.updateAvailable && !client.isUpdating;
                break;
            default:
                matchesStatus = true;
        }

        return matchesSearch && matchesStatus;
    });

    const eligibleCount = filteredClients.filter(
        c => c.updateAvailable && c.status === 'Online' && !c.isUpdating
    ).length;

    return (
        <Card elevation={2} sx={{ overflow: 'hidden', mt: 3 }}>
            <CardHeader
                avatar={<SystemUpdateIcon color="primary" />}
                title={<Typography variant="h6">OTA Update Management</Typography>}
                subheader={`${filteredClients.length} agents • ${eligibleCount} eligible for update`}
                action={
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            startIcon={<SystemUpdateIcon />}
                            onClick={() => setConfirmDialogOpen(true)}
                            disabled={selectedClients.length === 0 || updating}
                            size="small"
                        >
                            Update Selected ({selectedClients.length})
                        </Button>
                        <IconButton onClick={fetchClients} disabled={loading} size="small">
                            <RefreshIcon />
                        </IconButton>
                    </Stack>
                }
            />

            <Box sx={{ px: 2, pb: 2 }}>
                {/* Search and Filter */}
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Search agents..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                        sx={{ flexGrow: 1 }}
                    />
                    <Stack direction="row" spacing={1}>
                        {['all', 'updateAvailable', 'updating', 'upToDate'].map(filter => (
                            <Chip
                                key={filter}
                                label={filter === 'all' ? 'All' : filter === 'updateAvailable' ? 'Updates Available' : filter === 'updating' ? 'Updating' : 'Up to Date'}
                                onClick={() => setFilterStatus(filter)}
                                color={filterStatus === filter ? 'primary' : 'default'}
                                variant={filterStatus === filter ? 'filled' : 'outlined'}
                                size="small"
                            />
                        ))}
                    </Stack>
                </Stack>

                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {updateResults && (
                    <Alert
                        severity={updateResults.failedCount === 0 ? 'success' : 'warning'}
                        sx={{ mb: 2 }}
                        onClose={() => setUpdateResults(null)}
                    >
                        <Typography variant="body2" fontWeight={600}>
                            {updateResults.message}
                        </Typography>
                        <Typography variant="caption">
                            Triggered by: {updateResults.triggeredBy}
                        </Typography>
                    </Alert>
                )}

                {!loading && filteredClients.length === 0 && (
                    <Alert severity="info">No agents match the current filter.</Alert>
                )}

                {!loading && filteredClients.length > 0 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Tooltip title="Select all eligible">
                                            <Checkbox
                                                indeterminate={selectedClients.length > 0 && selectedClients.length < eligibleCount}
                                                checked={eligibleCount > 0 && selectedClients.length === eligibleCount}
                                                onChange={handleSelectAll}
                                                disabled={eligibleCount === 0}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>Agent</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Current Version</TableCell>
                                    <TableCell>Latest Version</TableCell>
                                    <TableCell>Update Status</TableCell>
                                    <TableCell align="right">History</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredClients.map((client) => {
                                    const canSelect = client.updateAvailable && client.status === 'Online' && !client.isUpdating;
                                    const isSelected = selectedClients.includes(client.id);
                                    const hasHistory = client.updateHistory && client.updateHistory.length > 0;

                                    return (
                                        <React.Fragment key={client.id}>
                                            <TableRow hover selected={isSelected}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={() => handleSelectClient(client.id)}
                                                        disabled={!canSelect}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {client.name || 'Unnamed'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {client.id}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={client.status}
                                                        color={getStatusColor(client.status)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {client.agentVersion || 'Unknown'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {client.latestVersion || 'Unknown'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        {getUpdateStatusIcon(client)}
                                                        <Box>
                                                            {client.isUpdating ? (
                                                                <>
                                                                    <Typography variant="caption" color="warning.main">
                                                                        Updating...
                                                                    </Typography>
                                                                    {client.updateProgress && (
                                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                                            {client.updateProgress.message}
                                                                        </Typography>
                                                                    )}
                                                                </>
                                                            ) : client.updateAvailable ? (
                                                                <Typography variant="caption" color="primary.main">
                                                                    Update Available
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="caption" color="success.main">
                                                                    Up to Date
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {hasHistory && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleHistoryExpanded(client.id)}
                                                        >
                                                            {expandedHistory[client.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>

                                            {/* Update History Row */}
                                            {hasHistory && (
                                                <TableRow>
                                                    <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedHistory[client.id] ? 1 : 0 }}>
                                                        <Collapse in={expandedHistory[client.id]} timeout="auto" unmountOnExit>
                                                            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                                                                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <HistoryIcon fontSize="small" />
                                                                    Update History
                                                                </Typography>
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell>Timestamp</TableCell>
                                                                            <TableCell>Triggered By</TableCell>
                                                                            <TableCell>Version</TableCell>
                                                                            <TableCell>Status</TableCell>
                                                                            <TableCell>Message</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {client.updateHistory.slice().reverse().map((entry, idx) => (
                                                                            <TableRow key={idx}>
                                                                                <TableCell>
                                                                                    <Typography variant="caption">
                                                                                        {new Date(entry.timestamp).toLocaleString()}
                                                                                    </Typography>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Typography variant="caption">
                                                                                        {entry.triggeredBy}
                                                                                    </Typography>
                                                                                    {entry.triggeredByEmail && (
                                                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                                                            {entry.triggeredByEmail}
                                                                                        </Typography>
                                                                                    )}
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Typography variant="caption">
                                                                                        {entry.fromVersion} → {entry.toVersion}
                                                                                    </Typography>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                                                        {getHistoryStatusIcon(entry.status)}
                                                                                        <Chip
                                                                                            label={entry.status}
                                                                                            size="small"
                                                                                            color={
                                                                                                entry.status === 'complete' ? 'success' :
                                                                                                    entry.status === 'error' ? 'error' : 'warning'
                                                                                            }
                                                                                        />
                                                                                    </Stack>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                        {entry.message || '-'}
                                                                                    </Typography>
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Confirm Bulk Update</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are about to trigger OTA updates for <strong>{selectedClients.length}</strong> agent(s).
                        This will restart the agents and may cause temporary disconnection.
                    </DialogContentText>
                    <DialogContentText sx={{ mt: 2 }}>
                        Are you sure you want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)} disabled={updating}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleBulkUpdate}
                        variant="contained"
                        color="primary"
                        disabled={updating}
                        startIcon={updating ? <CircularProgress size={16} /> : <SystemUpdateIcon />}
                    >
                        {updating ? 'Updating...' : 'Confirm Update'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}
